/**
 * Header Component
 *
 * Main header with navigation, search, and user menu.
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Logo } from './Logo';
import { SearchBar } from './SearchBar';
import { UserMenu } from './UserMenu';
import { MobileMenu } from './MobileMenu';
import { Container } from '../Layout/Container';

interface User {
  displayName?: string;
  firstName?: string;
  photoURL?: string;
  role?: 'guest' | 'host' | 'admin';
}

interface HeaderProps {
  transparent?: boolean;
  user?: User | null;
  isAuthenticated?: boolean;
  unreadMessages?: number;
  onLogout?: () => void;
}

export const Header: React.FC<HeaderProps> = ({
  transparent = false,
  user = null,
  isAuthenticated = false,
  unreadMessages = 0,
  onLogout = () => {},
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const showTransparent = transparent && !isScrolled;

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          showTransparent
            ? 'bg-transparent'
            : 'bg-white shadow-sm'
        }`}
      >
        <Container>
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Logo variant={showTransparent ? 'light' : 'dark'} />

            {/* Desktop Search - only visible when header has solid bg */}
            {!showTransparent && (
              <div className="hidden md:flex flex-1 justify-center max-w-xl mx-4">
                <SearchBar className="w-full max-w-md" />
              </div>
            )}

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-4">
              <Link
                to="/host"
                className={`px-4 py-2 rounded-full font-medium transition-colors ${
                  showTransparent
                    ? 'text-white hover:bg-white/10'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                Switch to Host
              </Link>

              {isAuthenticated && user ? (
                <UserMenu
                  user={user}
                  unreadMessages={unreadMessages}
                  onLogout={onLogout}
                />
              ) : (
                <div className="flex items-center gap-2">
                  <Link
                    to="/login"
                    className={`px-4 py-2 rounded-full font-medium transition-colors ${
                      showTransparent
                        ? 'text-white hover:bg-white/10'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    Log in
                  </Link>
                  <Link
                    to="/register"
                    className="px-4 py-2 bg-primary-500 text-white rounded-full font-medium hover:bg-primary-600 transition-colors"
                  >
                    Sign up
                  </Link>
                </div>
              )}
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
              onClick={() => setIsMobileMenuOpen(true)}
              aria-label="Open menu"
            >
              <MenuIcon
                className={`w-6 h-6 ${
                  showTransparent ? 'text-white' : 'text-gray-700'
                }`}
              />
            </button>
          </div>
        </Container>
      </header>

      {/* Mobile Menu */}
      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        user={user}
        isAuthenticated={isAuthenticated}
        onLogout={onLogout}
      />
    </>
  );
};

const MenuIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 6h16M4 12h16M4 18h16"
    />
  </svg>
);

export default Header;
